import { buildHeaders, buildUrl } from "./apiConfig";
import { DEFAULT_PREVIEW_TEXTS, ENGINE_LABEL_MAP, ENGINE_STYLE_MAP } from "../constants";

export { DEFAULT_PREVIEW_TEXTS };

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str);

const normalizeVoices = (data) => {
  const result = {};

  Object.entries(data).forEach(([lang, voices]) => {
    const expanded = [];

    voices.forEach((v) => {
      // Handle both AWS PascalCase (Id, Name, Gender) and camelCase (id, name, gender)
      const id = v.Id || v.id;
      const name = v.Name || v.name;
      const gender = capitalize(v.Gender || v.gender);

      // SupportedEngines array — present in raw AWS Polly API responses
      const supportedEngines = v.SupportedEngines || v.supportedEngines || v.engines;

      if (Array.isArray(supportedEngines) && supportedEngines.length > 0) {
        supportedEngines.forEach((eng) => {
          const engineLabel = ENGINE_LABEL_MAP[eng?.toLowerCase()] ?? capitalize(eng);
          expanded.push({
            id,
            name,
            gender,
            engine: engineLabel,
            style: ENGINE_STYLE_MAP[engineLabel] || "Standard",
          });
        });
      } else {
        const engineLabel = ENGINE_LABEL_MAP[v.engine?.toLowerCase()] ?? capitalize(v.engine);
        expanded.push({
          id,
          name,
          gender,
          engine: engineLabel,
          style: ENGINE_STYLE_MAP[engineLabel] || "Standard",
        });
      }
    });

    result[lang] = expanded;
  });

  return result;
};

export const fetchPollyVoices = async (languages) => {
  try {
    const url = buildUrl("");

    const response = await fetch(url, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ action: "listVoices", languages }),
    });

    if (!response.ok) {
      throw new Error(`API Gateway responded with status ${response.status}`);
    }

    const json = await response.json();

    if (!json.success) {
      throw new Error(json.error || "Unknown error from Polly Lambda");
    }

    if (json && json.data && json.data["Dutch"]) {
      json.data["Dutch"] = json.data["Dutch"].filter((item) => item.id !== "Ruben");
    }
    const normalized = normalizeVoices(json.data);

    return normalized;
  } catch (err) {
    console.error("[Polly] fetchPollyVoices error:", err.message);
    return {};
  }
};

export const synthesizePollyVoice = async (voiceId, text, engine = "standard") => {
  try {
    const url = buildUrl("");

    const response = await fetch(url, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({
        action: "synthesizeSpeech",
        voiceId,
        text,
        engine,
      }),
    });

    if (!response.ok) {
      throw new Error(`API Gateway responded with status ${response.status}`);
    }

    const json = await response.json();

    if (!json.success) {
      throw new Error(json.error || "Unknown error from Polly Lambda");
    }

    // Convert Base64 audio → playable Blob → play in browser
    const audioBytes = Uint8Array.from(atob(json.audio), (c) => c.charCodeAt(0));
    const audioBlob = new Blob([audioBytes], { type: "audio/mpeg" });
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);

    await new Promise((resolve, reject) => {
      audio.onended = resolve;
      audio.onerror = reject;
      audio.play();
    });

    URL.revokeObjectURL(audioUrl);
  } catch (err) {
    console.error("[Polly] synthesizePollyVoice error:", err.message);
    throw err;
  }
};
